<template>
  <component
    :is="isLink ? SFLink : 'div'"
    v-bind="
      isLink
        ? {
            to: pathParams?.route,
            target: pathParams?.openInNew ? '_blank' : '_self',
            variant,
            raw: true,
          }
        : {}
    "
    :class="{
      '!m-0 block w-fit content-center rounded pb-3  font-normal text-[var(--textColor)] transition-all  supports-hover:hover:text-red-500 md:text-md xl:text-lg':
        !raw && isLink,
      '!m-0 !text-red-500': isActive && !raw && isLink,
      '!m-0 min-h-5 w-fit p-1 font-normal leading-5 text-[var(--textColor)] md:text-md xl:text-lg':
        !raw && !isLink,
    }"
    :style="style"
    @mouseenter="emit('mouseenterNavigationItem')"
  >
    <div class="flex items-center gap-3" data-testid="navigation-item">
      <!-- Navigation category image (mobile submenu only) -->
      <template v-if="showNavigationImage && navigationImagePath">
        <!-- Show image if it loads successfully -->
        <NuxtImg
          v-if="!imageHasError"
          :src="navigationImagePath"
          :alt="navigationItem.name"
          class="pointer-events-none size-16 rounded object-contain"
          loading="lazy"
          @error="onImageError"
        />
        <!-- Show placeholder div if image fails to load -->
        <div
          v-else
          class="pointer-events-none size-16 rounded bg-gray-200"
          :title="`Image not found for ${navigationItem.name}`"
        ></div>
      </template>

      <!-- Icon fallback (if no navigation image but has icon) -->
      <template v-else-if="iconPath">
        <ScayleImg
          :src="iconPath"
          :aria-labelledby="`${navigationItem.id}`"
          aria-hidden="true"
          class="pointer-events-none size-4"
          tabindex="-1"
          preload
        />
      </template>

      <!-- Navigation text -->
      <span :id="`${navigationItem.id}`">
        <slot>
          {{ displayName }}
        </slot>
      </span>
    </div>
  </component>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import type { NavigationTreeItem } from '@scayle/storefront-nuxt'
import Color from 'color'
import { NuxtImg, ScayleImg } from '#components'
import type { LinkVariant } from '#storefront-ui'
import { useRouteHelpers } from '~/composables'
import { SFLink } from '#storefront-ui/components'
import { theme } from '#tailwind-config'

const {
  navigationItem,
  raw = false,
  isActive = false,
  textColor,
  backgroundColor,
  disabledLink,
  showNavigationImage = false,
} = defineProps<{
  navigationItem: NavigationTreeItem
  variant?: LinkVariant
  raw?: boolean
  isActive?: boolean
  disabledLink?: boolean
  textColor?: `#${string}`
  backgroundColor?: `#${string}`
  showNavigationImage?: boolean
}>()

const { buildNavigationTreeItemRoute } = useRouteHelpers()

const iconPath = computed(() => {
  const assets = Object.values(navigationItem?.assets ?? {})
  const icon = assets.find((asset) => asset.endsWith('svg'))

  if (!icon) {
    return
  }

  // For usage with `<object>` tag within the template use the following
  // `return URL.parse(icon, cdnUrl)?.toString()`, where
  // `cdnUrl` can be sourced from `useRuntimeConfig().public`.
  return icon
})

const navigationImagePath = computed(() => {
  if (!navigationItem?.id) {
    return null
  }

  // Images are now simply named with just the ID: {id}.png
  return `/navigation/stg/${navigationItem.id}.png`
})

const imageHasError = ref(false)

const onImageError = () => {
  imageHasError.value = true
}

const emit = defineEmits<{ mouseenterNavigationItem: [] }>()

const pathParams = computed(() => {
  return buildNavigationTreeItemRoute(navigationItem)
})
const displayName = computed(() => navigationItem.name)
const disabled = computed<boolean>(() =>
  disabledLink ? disabledLink : !!navigationItem.customData?.disabledLink,
)
const getStyle = (
  navigationTreeItem: NavigationTreeItem,
  fallbackBackgroundColor: `#${string}`,
  fallbackTextColor: `#${string}`,
) => {
  const linkColor = navigationTreeItem?.customData?.linkColor

  if (!linkColor) {
    return {
      '--backgroundColor': Color(fallbackBackgroundColor).hex(),
      '--textColor': Color(fallbackTextColor).hex(),
    }
  }

  return {
    '--backgroundColor': Color(linkColor).alpha(0.1).hexa(),
    '--textColor': Color(linkColor).hex(),
  }
}

const isLink = computed(() => {
  return pathParams.value?.route && !disabled.value
})

const style = computed(() => {
  return getStyle(
    navigationItem,
    backgroundColor ?? theme.colors.gray[100],
    textColor ??
      (isLink.value ? theme.colors.gray[600] : theme.colors.gray[900]),
  )
})
</script>
